import { FileCopy } from '@mui/icons-material'
import { Box, ButtonGroup } from '@mui/material'
import Button from '@mui/material/Button'
import { getCallsToAction } from '@promoboxx/redux-stores/src/stores/facebook-ads/actions'
import { sortedCallsToActionListSelector } from '@promoboxx/redux-stores/src/stores/facebook-ads/selectors'
import { Field, getIn } from 'formik'
import { TextField } from 'formik-mui'
import I18n from 'i18n-js'
import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import FormikErrorChecker from '@src/lib/FormikErrorChecker'
import MenuItemWithCheckbox from '@src/lib/MenuItemWithCheckbox'

const EMPTY_ARRAY = []
const EMPTY_OBJECT = {}

const RetailerOverrides = (props) => {
  const dispatch = useDispatch()
  useEffect(() => {
    dispatch(getCallsToAction())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const callsToAction = useSelector(sortedCallsToActionListSelector)
  const { field } = props
  const adCreativeTemplate = field.value || EMPTY_OBJECT
  const childAttachments =
    adCreativeTemplate && adCreativeTemplate.creative
      ? adCreativeTemplate.creative.child_attachments
      : EMPTY_ARRAY
  const isCarousel = adCreativeTemplate.creative_type === 'carousel'
  const selectedIndex = props.form.values.carouselIndex
  const setSelectedIndex = (i) => {
    props.form.setFieldValue('carouselIndex', i)
  }

  const selector = useMemo(() => {
    if (!adCreativeTemplate) {
      return null
    }

    if (adCreativeTemplate.creative_type !== 'carousel') {
      return null
    }

    const buttons = Array(childAttachments.length)
      .fill(null)
      .map((_x, i) => (
        <FormikErrorChecker
          fields={[`${field.name}.creative.child_attachments[${i}]`]}
        >
          {(childAttachmentErrors) => {
            return (
              <Button
                color={childAttachmentErrors.areAllValid ? 'primary' : 'error'}
                variant={i === selectedIndex ? 'contained' : 'outlined'}
                onClick={() => setSelectedIndex(i)}
              >
                {i + 1}
              </Button>
            )
          }}
        </FormikErrorChecker>
      ))

    return <ButtonGroup>{buttons}</ButtonGroup>
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adCreativeTemplate.creative_type, childAttachments, selectedIndex])

  if (!field.value) {
    return null
  }

  const linkType =
    adCreativeTemplate.creative_type === 'carousel'
      ? getIn(
          adCreativeTemplate,
          `creative.child_attachments[${selectedIndex}].link.link_type`,
        )
      : getIn(adCreativeTemplate, `creative.link.link_type`)

  // Not so fast, bucko! You'd think a checkbox called "allow url overrides"
  // would ... allow url overrides but nah! It also depends on the link type, or
  // the current carousel item's link type.
  const allowUrlOverride =
    linkType === 'retailer_website' &&
    adCreativeTemplate.allow_url_override === true
  const allowCtaOverride =
    linkType === 'retailer_website' &&
    adCreativeTemplate.allow_call_to_action_override === true

  const urlFieldName = isCarousel
    ? `${field.name}.creative.child_attachments[${selectedIndex}].link.url`
    : `${field.name}.creative.link.url`

  const urlField = (
    <Field
      component={TextField}
      disabled={!allowUrlOverride || props.disabled}
      fullWidth
      helperText={
        !allowUrlOverride && 'Overrides have been disabled by the brand'
      }
      label="URL"
      name={urlFieldName}
    />
  )

  const ctaFieldName = isCarousel
    ? `${field.name}.creative.child_attachments[${selectedIndex}].call_to_action.cta_type`
    : `${field.name}.creative.call_to_action.cta_type`

  const ctaField = (
    <Field
      component={TextField}
      disabled={!allowCtaOverride || props.disabled}
      fullWidth
      helperText={
        !allowCtaOverride && 'Overrides have been disabled by the brand'
      }
      label={I18n.t('shared.callToAction')}
      name={ctaFieldName}
      select
    >
      {callsToAction.map((cta) => (
        <MenuItemWithCheckbox key={cta.code} value={cta.code}>
          {cta.value}
        </MenuItemWithCheckbox>
      ))}
    </Field>
  )

  const handleApplyToAllClick = () => {
    const item = field.value.creative.child_attachments[selectedIndex]
    const newValue = field.value.creative.child_attachments.map((x) => ({
      ...x,
      call_to_action: item.call_to_action,
      link: item.link,
    }))
    field.onChange(`${field.name}.creative.child_attachments`)(newValue)
  }

  return (
    <div>
      {selector}
      {urlField}
      {callsToAction.length > 0 && ctaField}
      {isCarousel ? (
        <Box textAlign="right">
          <Button
            color="secondary"
            startIcon={<FileCopy />}
            variant="outlined"
            onClick={() => {
              handleApplyToAllClick()
            }}
          >
            {I18n.t('content.applyToAllCards')}
          </Button>
        </Box>
      ) : undefined}
    </div>
  )
}

export default RetailerOverrides
